import { useRef, useState, useCallback, useEffect } from 'react';
import { API_BASE_URL } from '@/config';

/* ─── tipos ─── */
export type VoiceStatus =
  | 'disconnected'
  | 'connecting'
  | 'listening'
  | 'user_speaking'
  | 'thinking'
  | 'speaking'
  | 'error';

export interface VoiceMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

export interface VoiceInfo {
  id: string;
  label: string;
  gender: 'F' | 'M' | 'N';
}

export const VOICES: VoiceInfo[] = [
  { id: 'coral',   label: 'Coral (default)', gender: 'F' },
  { id: 'alloy',   label: 'Alloy',           gender: 'N' },
  { id: 'ash',     label: 'Ash',             gender: 'M' },
  { id: 'ballad',  label: 'Ballad',          gender: 'F' },
  { id: 'echo',    label: 'Echo',            gender: 'M' },
  { id: 'sage',    label: 'Sage',            gender: 'F' },
  { id: 'shimmer', label: 'Shimmer',         gender: 'F' },
  { id: 'verse',   label: 'Verse',           gender: 'N' },
];

let msgCounter = 0;
const uid = () => `vm_${++msgCounter}_${Date.now()}`;

/* ─── utilidades base64 ↔ ArrayBuffer ─── */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

/* ───────────────────────────────────────────────────────────
   Hook: Realtime Voice — AudioWorklet PCM16 streaming,
   el backend actúa como relay con OpenAI Realtime API.
   VAD, STT, LLM, TTS y interrupciones se manejan server-side.
   ─────────────────────────────────────────────────────────── */
export function useVoiceWebSocket(orgId?: string) {
  const resolvedOrg = orgId || localStorage.getItem('org_id') || 'orion';

  /* refs */
  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const playbackContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<AudioWorkletNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isMutedRef = useRef(false);
  const playbackQueueRef = useRef<AudioBuffer[]>([]);
  const isPlayingRef = useRef(false);
  const nextPlayTimeRef = useRef(0);
  const activeSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const suppressPlaybackRef = useRef(false);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>();
  const pingTimer = useRef<ReturnType<typeof setInterval>>();
  const isChannelOpenRef = useRef(false);
  const statusRef = useRef<VoiceStatus>('disconnected');

  /* state */
  const [status, setStatus] = useState<VoiceStatus>('disconnected');
  const [messages, setMessages] = useState<VoiceMessage[]>([]);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [selectedVoice, setSelectedVoice] = useState(VOICES[0].id);
  const [isConnected, setIsConnected] = useState(false);
  const [isChannelOpen, setIsChannelOpen] = useState(false);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* ═══════ Audio Playback (24 kHz PCM16 streaming) ═══════ */
  const stopPlayback = useCallback(() => {
    // Marcar para ignorar audio_deltas en vuelo
    suppressPlaybackRef.current = true;
    // Detener todos los source nodes activos inmediatamente
    activeSourcesRef.current.forEach(src => {
      try { src.stop(0); } catch { /* ya terminó */ }
    });
    activeSourcesRef.current = [];
    playbackQueueRef.current = [];
    nextPlayTimeRef.current = 0;
    isPlayingRef.current = false;
  }, []);

  const playNextChunk = useCallback(() => {
    const ctx = playbackContextRef.current;
    if (!ctx || playbackQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      return;
    }
    isPlayingRef.current = true;
    const buffer = playbackQueueRef.current.shift()!;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    const now = ctx.currentTime;
    const startTime = Math.max(now, nextPlayTimeRef.current);
    src.start(startTime);
    nextPlayTimeRef.current = startTime + buffer.duration;

    // Trackear para poder detenerlo en interrupción
    activeSourcesRef.current.push(src);
    src.onended = () => {
      activeSourcesRef.current = activeSourcesRef.current.filter(s => s !== src);
      playNextChunk();
    };
  }, []);

  const handleAudioDelta = useCallback((base64Audio: string) => {
    // Ignorar audio que llega después de una interrupción
    if (suppressPlaybackRef.current) return;
    const ctx = playbackContextRef.current;
    if (!ctx) return;
    // Asegurar que el contexto esté activo (Chrome lo suspende)
    if (ctx.state === 'suspended') ctx.resume();
    const pcm16 = new Int16Array(base64ToArrayBuffer(base64Audio));
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) float32[i] = pcm16[i] / 32768.0;
    const audioBuffer = ctx.createBuffer(1, float32.length, 24000);
    audioBuffer.getChannelData(0).set(float32);
    playbackQueueRef.current.push(audioBuffer);
    if (!isPlayingRef.current) playNextChunk();
  }, [playNextChunk]);

  /* ═══════ WS Message handler ═══════ */
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data as string);
      switch (data.type) {
        case 'connected':
          setSessionId(data.session_id);
          console.log('[Voice RT] Session:', data.session_id);
          break;

        case 'ready':
          setStatus('listening');
          statusRef.current = 'listening';
          console.log('[Voice RT] Ready — voice:', data.voice, 'tools:', data.tools_count);
          break;

        case 'speech_started':
          // SIEMPRE parar la reproducción — el usuario quiere hablar
          stopPlayback();
          // Cancelar respuesta del agente en el servidor
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ action: 'cancel' }));
          }
          setCurrentTranscript('');
          setStatus('user_speaking');
          statusRef.current = 'user_speaking';
          isMutedRef.current = false;
          break;

        case 'speech_stopped':
          setStatus('thinking');
          statusRef.current = 'thinking';
          break;

        case 'transcript':
          if (data.role === 'user' && data.text?.trim()) {
            setMessages(prev => [...prev, { id: uid(), role: 'user', text: data.text }]);
          }
          break;

        case 'transcript_delta':
          if (data.role === 'assistant') {
            setCurrentTranscript(prev => prev + (data.delta || ''));
          }
          break;

        case 'response_text':
          if (data.text?.trim()) {
            setMessages(prev => [...prev, { id: uid(), role: 'assistant', text: data.text }]);
            setCurrentTranscript('');
          }
          break;

        case 'audio_delta':
          handleAudioDelta(data.audio);
          break;

        case 'audio_done':
          // Ya no hay más audio del agente para esta respuesta
          suppressPlaybackRef.current = false;
          break;

        case 'status':
          if (data.status === 'listening') {
            setStatus('listening');
            statusRef.current = 'listening';
            setTimeout(() => { isMutedRef.current = false; }, 150);
          } else if (data.status === 'thinking') {
            setStatus('thinking');
            statusRef.current = 'thinking';
            isMutedRef.current = true;
          } else if (data.status === 'speaking') {
            setStatus('speaking');
            statusRef.current = 'speaking';
            isMutedRef.current = true;
            // Nueva respuesta del agente — permitir reproducción
            suppressPlaybackRef.current = false;
          }
          break;

        case 'tool_call':
          if (data.status === 'executing') setActiveTool(data.name);
          else setActiveTool(null);
          break;

        case 'config_updated':
          console.log('[Voice RT] Config updated — voice:', data.voice);
          break;

        case 'error':
          console.warn('[Voice RT] Error:', data.message, data.code);
          // Ignorar errores de cancelación sin respuesta activa
          if (data.message?.includes('no active response')) break;
          setError(data.message || 'Error del servidor');
          setStatus('error');
          statusRef.current = 'error';
          setTimeout(() => { setStatus('listening'); statusRef.current = 'listening'; }, 2000);
          break;

        case 'pong':
          break;

        default:
          console.log('[Voice RT] Unknown:', data);
      }
    } catch (e) {
      console.warn('[Voice RT] Parse error:', e);
    }
  }, [handleAudioDelta, stopPlayback]);

  /* ═══════ Build WS URL (Realtime endpoint) ═══════ */
  const buildUrl = useCallback(() => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    if (API_BASE_URL.startsWith('/')) {
      return `${proto}://${window.location.host}${API_BASE_URL.replace(/\/$/, '')}/ws/voice/realtime`;
    }
    const base = API_BASE_URL.replace(/^https?:\/\//, '').replace(/\/$/, '');
    return `${proto}://${base}/ws/voice/realtime`;
  }, []);

  /* ═══════ Connect WebSocket (lazy — no auto-connect) ═══════ */
  const connectWs = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
    }
    const url = buildUrl();
    console.log('[Voice RT] Connecting to', url);
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      setStatus('connecting');
      setError(null);

      ws.send(JSON.stringify({
        action: 'start',
        org_id: resolvedOrg,
        telefono: localStorage.getItem('user_phone') || '',
        thread_id: null,
        voice: selectedVoice,
        // 🛡️ Admin identity via JWT — backend hace verify_token + lookup User por user_id.
        // Brain parity con el chat: Orión sabe quién es el admin que habla por voz.
        auth_token: localStorage.getItem('auth_token') || null,
      }));

      pingTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: 'ping' }));
      }, 30_000);
    };

    ws.onmessage = handleMessage;
    ws.onerror = (e) => console.error('[Voice RT] Error:', e);

    ws.onclose = () => {
      setIsConnected(false);
      if (isChannelOpenRef.current) {
        setStatus('disconnected');
        reconnectTimer.current = setTimeout(connectWs, 3000);
      } else {
        setStatus('disconnected');
      }
      if (pingTimer.current) clearInterval(pingTimer.current);
    };
  }, [buildUrl, resolvedOrg, selectedVoice, handleMessage]);

  /* ═══════ Audio Capture — AudioWorklet PCM16 @ 16 kHz ═══════ */
  const startMic = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 16000,
        channelCount: 1,
      },
    });
    streamRef.current = stream;

    const audioCtx = new AudioContext({ sampleRate: 16000 });
    audioContextRef.current = audioCtx;

    // Worklet path derived from Vite's BASE_URL so it tracks vite.config.ts.
    const workletUrl = `${import.meta.env.BASE_URL}audio-processor.js`;
    await audioCtx.audioWorklet.addModule(workletUrl);

    const source = audioCtx.createMediaStreamSource(stream);
    sourceRef.current = source;

    const processor = new AudioWorkletNode(audioCtx, 'pcm16-processor');
    processorRef.current = processor;

    processor.port.onmessage = (e: MessageEvent) => {
      if (wsRef.current?.readyState !== WebSocket.OPEN || isMutedRef.current) return;
      const pcm16: Int16Array = e.data;
      const base64 = arrayBufferToBase64(pcm16.buffer as ArrayBuffer);
      wsRef.current.send(JSON.stringify({ action: 'audio', audio: base64 }));
    };

    source.connect(processor);
    // NO conectar processor a destination para evitar feedback

    // Crear contexto de reproducción (24 kHz) y activarlo
    const playCtx = new AudioContext({ sampleRate: 24000 });
    if (playCtx.state === 'suspended') await playCtx.resume();
    playbackContextRef.current = playCtx;

    // También asegurar que el contexto de captura esté activo
    if (audioCtx.state === 'suspended') await audioCtx.resume();
  }, []);

  const stopMic = useCallback(() => {
    processorRef.current?.disconnect();
    processorRef.current = null;
    sourceRef.current?.disconnect();
    sourceRef.current = null;
    streamRef.current?.getTracks().forEach((t: MediaStreamTrack) => t.stop());
    streamRef.current = null;
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    playbackContextRef.current?.close().catch(() => {});
    playbackContextRef.current = null;
    stopPlayback();
  }, [stopPlayback]);

  /* ═══════ Abrir canal (WS + mic) ═══════ */
  const openChannel = useCallback(async () => {
    if (isChannelOpenRef.current) return;
    setError(null);

    try {
      isChannelOpenRef.current = true;
      setIsChannelOpen(true);
      connectWs();
      await startMic();
    } catch (e: any) {
      isChannelOpenRef.current = false;
      setIsChannelOpen(false);
      setError('No se pudo acceder al micrófono: ' + (e.message || e));
    }
  }, [connectWs, startMic]);

  /* ═══════ Cerrar canal ═══════ */
  const closeChannel = useCallback(() => {
    isChannelOpenRef.current = false;
    setIsChannelOpen(false);
    setStatus('disconnected');

    stopMic();
    isMutedRef.current = false;

    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    if (pingTimer.current) clearInterval(pingTimer.current);
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [stopMic]);

  /* ═══════ Cleanup on unmount ═══════ */
  useEffect(() => {
    return () => {
      isChannelOpenRef.current = false;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (pingTimer.current) clearInterval(pingTimer.current);
      stopMic();
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ═══════ Enviar texto (fallback) ═══════ */
  const sendText = useCallback((text: string) => {
    if (!text.trim() || wsRef.current?.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ action: 'text', text }));
    setMessages(prev => [...prev, { id: uid(), role: 'user', text }]);
    setStatus('thinking');
  }, []);

  /* ═══════ Cambiar voz en caliente ═══════ */
  const changeVoice = useCallback((voice: string) => {
    setSelectedVoice(voice);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'config', voice }));
    }
  }, []);

  /* ═══════ Interrumpir agente manualmente ═══════ */
  const interrupt = useCallback(() => {
    stopPlayback();
    isMutedRef.current = false;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'cancel' }));
    }
  }, [stopPlayback]);

  const clearMessages = useCallback(() => { setMessages([]); setCurrentTranscript(''); }, []);

  return {
    status,
    messages,
    currentTranscript,
    isConnected,
    isChannelOpen,
    selectedVoice,
    activeTool,
    sessionId,
    error,
    openChannel,
    closeChannel,
    sendText,
    changeVoice,
    interrupt,
    clearMessages,
  };
}
